import type { MarketplaceStats } from '@/hooks/useSuperStats';

interface ContentsStatsProps {
  data: MarketplaceStats;
  loading: boolean;
}

interface StatDef {
  label: string;
  value: number;
  icon: string;
  iconClass: string;
}

/**
 * Contenus & publications de la plateforme : annonces marketplace
 * (produits / services), articles publiés, produits boutique et services.
 */
export default function ContentsStats({ data, loading }: ContentsStatsProps) {
  const stats: StatDef[] = [
    {
      label: 'Annonces marketplace',
      value: data.annoncesTotal,
      icon: 'ri-store-2-line',
      iconClass: 'bg-accent-100 text-accent-700',
    },
    {
      label: 'Produits marketplace',
      value: data.annoncesProducts,
      icon: 'ri-shopping-bag-3-line',
      iconClass: 'bg-primary-100 text-primary-700',
    },
    {
      label: 'Services marketplace',
      value: data.annoncesServices,
      icon: 'ri-briefcase-4-line',
      iconClass: 'bg-secondary-100 text-secondary-700',
    },
    {
      label: 'Annonces publiées (période)',
      value: data.annoncesPublished,
      icon: 'ri-send-plane-line',
      iconClass: 'bg-accent-100 text-accent-700',
    },
    {
      label: 'Produits boutique',
      value: data.productItems,
      icon: 'ri-shopping-bag-2-line',
      iconClass: 'bg-primary-100 text-primary-700',
    },
    {
      label: 'Produits numériques',
      value: data.digitalProducts,
      icon: 'ri-download-cloud-2-line',
      iconClass: 'bg-secondary-100 text-secondary-700',
    },
    {
      label: 'Services des sites',
      value: data.servicesItems,
      icon: 'ri-service-line',
      iconClass: 'bg-accent-100 text-accent-700',
    },
  ];

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 mb-5 md:mb-6">
      <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2 mb-4">
        <i className="ri-stack-line text-accent-500"></i>
        Contenus &amp; publications
      </h3>

      {loading ? (
        <div className="flex items-center justify-center h-24">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-300"></i>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="rounded-lg bg-background-100 border border-background-200/60 p-3.5"
            >
              <span
                className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${stat.iconClass}`}
              >
                <i className={stat.icon}></i>
              </span>
              <div className="text-xl font-bold font-heading text-foreground-950">
                {stat.value.toLocaleString('fr-FR')}
              </div>
              <div className="text-[11px] text-foreground-500 leading-snug mt-0.5">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}